import { useCallback, useMemo, useState } from 'react';

import { selectedDepositAmount } from '@/lib/quote/selectedDeposit';
import {
  documentSavingsGap,
  keptUnalignedDiscountIndices,
  selectedMembershipDiscount,
  selectedNonMembershipDiscount,
} from '@/lib/sales/estimate-discount-rows';
import { rendersOnCustomerDocument } from '@/lib/sales/line-economics';
import { resolveEstimateLineAmounts } from '@/lib/sales/line-item-pricing';
import { optionalKey } from '@/lib/sales/optional-line-key';
import {
  membershipOfferForSelectedLines,
  membershipOfferFromSnapshot,
  membershipOfferOriginalSelectionPricing,
} from '@/lib/sales/membership-offer';
import { isMembershipPurchaseLine } from '@notifi/shared-types/membership-line';
import {
  customerFacingLines,
  customerVisibleRows,
  offerCountedLines,
  splitOptionalOffers,
} from '@/lib/sales/service-components';
import type { Estimate } from '@/types/estimate';

/**
 * The customer-facing estimate SELECTION ENGINE, shared by both surfaces that
 * let the customer toggle optional add-ons and reprice live: the public quote
 * page (`quote.$id.tsx`) and the logged-in portal estimate detail. Lifted
 * verbatim out of the public quote so a single implementation owns "which lines
 * are in the sale, and what do they total" — the two surfaces cannot drift.
 *
 * Pure: depends only on the `estimate` payload and the local optional-selection
 * state it owns. Every money/approval path reads `selectedEstimate` / `totals`,
 * so a caller never recomputes the sale a second, disagreeing way.
 */
export function useEstimateSelections(estimate: Estimate) {
  // Defensive: the Estimate type declares `lineItems` non-null and the backend
  // defaults it to `[]`, but a malformed/legacy payload with null lineItems would
  // otherwise crash both surfaces in splitOptionalOffers. Normalise once; when
  // present this is the same array reference, so memoization is unaffected.
  const lineItems = estimate.lineItems ?? [];
  // NOT-590 — only a TOP-LEVEL line is something the customer chooses.
  // `isOptional` on a PART means the operator offered it inside a service; it
  // does not make the part a separate thing to buy. Splitting the flat list on
  // that flag tore the part away from its service, and the orphan was then
  // promoted to its own top-level row — an unselected add-on printed on the
  // quote as a standalone priced line, with a checkbox of its own, that no
  // total accounted for. Which lines COUNT is unchanged; see splitOptionalOffers.
  const { required: requiredItems, offers: optionalOffers } = useMemo(
    () => splitOptionalOffers(lineItems),
    [lineItems],
  );

  // The customer's explicit optional-add-on OVERRIDES, keyed by optionalKey —
  // only the deltas from what the estimate stored. The resolved map below layers
  // these over each offer's own `isSelected`, so an offer with no override always
  // falls back to its stored default. That fallback is what lets the SAME mounted
  // hook survive its `estimate` prop being swapped or refetched with a DIFFERENT
  // set of offers (both surfaces keep it mounted while react-query changes data):
  // a new or changed offer resolves from its own `isSelected`, never a stale entry.
  const [overrides, setOverrides] = useState<Record<string, boolean>>({});

  // Clear overrides when a DIFFERENT estimate arrives so one document's picks can
  // never leak into another through a colliding key. Not needed for defaults (the
  // fallback handles those) — only to keep the map bounded. Keyed on identity, so
  // a post-approval reprice of the SAME estimate keeps the customer's picks.
  const [overridesForId, setOverridesForId] = useState(estimate.id);
  if (estimate.id !== overridesForId) {
    setOverridesForId(estimate.id);
    setOverrides({});
  }

  // Resolve each CURRENT offer's selection: an explicit override wins, else the
  // line's stored `isSelected` (an absent flag reads as selected, matching the
  // backend). Rebuilt from the live offers every render, so it always describes
  // the offers actually on screen — never a previous estimate's.
  const optionalSelections = useMemo<Record<string, boolean>>(() => {
    const resolved: Record<string, boolean> = {};
    for (const [index, { parent: li }] of optionalOffers.entries()) {
      const key = optionalKey(li, index);
      resolved[key] = overrides[key] ?? li.isSelected !== false;
    }
    return resolved;
  }, [optionalOffers, overrides]);

  // Re-file parts between the base-sale table and their optional service as
  // the customer ticks that service. This is a DISPLAY-ONLY overlay: cloning
  // only the offer parents lets splitOptionalOffers see the live selection,
  // while every money/approval path below keeps the original line identities
  // in `requiredItems`, `optionalOffers`, and `selectedLines`.
  const { requiredRows, displayOptionalOffers } = useMemo(() => {
    const liveOfferSelections = new Map(
      optionalOffers.map((offer, index) => [
        offer.parent,
        Boolean(optionalSelections[optionalKey(offer.parent, index)]),
      ]),
    );
    const displayLines = lineItems.map((line) =>
      liveOfferSelections.has(line)
        ? { ...line, isSelected: liveOfferSelections.get(line)! }
        : line,
    );
    const displaySplit = splitOptionalOffers(displayLines);
    return {
      // Display only — `selectedLines` below owns what the customer is charged,
      // so a row dropped here is still in every total on the page.
      requiredRows: customerVisibleRows(customerFacingLines(displaySplit.required)),
      // The same rule, applied to the parts listed under each offer: an
      // unbilled consumable has no business on the customer's page whichever
      // heading it happens to sit under. Display only, like the rows above —
      // nothing here moves a total, and an offer whose parts all drop simply
      // lists none. `parts` is untouched: the card prices what the checkbox
      // moves, and a declined service takes its billed parts with it
      // (`offerCountedLines`).
      displayOptionalOffers: displaySplit.offers.map((offer) => ({
        ...offer,
        components: offer.components.filter(rendersOnCustomerDocument),
      })),
    };
  }, [lineItems, optionalOffers, optionalSelections]);

  const toggleOptional = (key: string) => {
    // Record the flip of the CURRENTLY RESOLVED value as an explicit override, so
    // toggling an offer that was defaulting to its stored `isSelected` records the
    // opposite of that default rather than the opposite of an absent entry.
    setOverrides((prev) => ({ ...prev, [key]: !optionalSelections[key] }));
  };
  const optionalSelectionsChanged = optionalOffers.some(
    ({ parent: li }, index) =>
      !!optionalSelections[optionalKey(li, index)] !== (li.isSelected !== false),
  );

  // The lines currently in the sale: everything the customer does not choose,
  // plus every offer they have ticked.
  const selectedLines = useMemo(
    () => [
      ...requiredItems,
      ...optionalOffers.flatMap((offer, index) =>
        offerCountedLines(offer, Boolean(optionalSelections[optionalKey(offer.parent, index)])),
      ),
    ],
    [requiredItems, optionalOffers, optionalSelections],
  );

  // Persisted row taxes/document discounts describe the original selection.
  // A live optional change must allocate them again using the frozen offer's
  // exact original-side pricing, just as the member comparison already does.
  const selectionPricing = useMemo(() => {
    if (!optionalSelectionsChanged || !['SENT', 'VIEWED', 'FOLLOW_UP'].includes(estimate.status)) return null;
    const offer = membershipOfferFromSnapshot(estimate.membershipPlanSnapshot);
    if (!offer?.selectionPricing) return null;
    const preview = membershipOfferOriginalSelectionPricing(
      offer, selectedLines.filter((line) => !isMembershipPurchaseLine(line)), lineItems,
    );
    if (!preview) return null;
    const byLine = new Map<Estimate['lineItems'][number], { lineTax: number; lineTotal: number }>();
    for (const [index, row] of offer.selectionPricing.lines.entries()) {
      const source = row.lineKey
        ? lineItems.find((line) => line.lineKey === row.lineKey)
        : lineItems[row.lineIndex];
      const amount = preview.lineItemPreviews[index];
      if (source && amount && !isMembershipPurchaseLine(source)) {
        byLine.set(source, { lineTax: amount.lineTax, lineTotal: amount.lineTotal });
      }
    }
    return byLine;
  }, [estimate.status, estimate.membershipPlanSnapshot, optionalSelectionsChanged, selectedLines, lineItems]);

  // Recalculate totals based on selections
  const totals = useMemo(() => {
    const amounts = selectedLines.map((line) => ({
      ...resolveEstimateLineAmounts(line),
      ...selectionPricing?.get(line),
    }));
    const subtotal = amounts.reduce((sum, line) => sum + line.lineSubtotal, 0);
    const taxTotal = amounts.reduce((sum, line) => sum + line.lineTax, 0);
    const total = amounts.reduce((sum, line) => sum + line.lineTotal, 0);
    return { subtotal, taxTotal, total };
  }, [selectedLines, selectionPricing]);

  // The subtotal split into scope-vs-add-on so a summary can label each honestly
  // instead of binding the scope's name to a figure inflated by ticked extras.
  // Uses the SAME primitives (`offerCountedLines` + `resolveEstimateLineAmounts`)
  // that produced `totals.subtotal`, so scope + Σ add-ons === totals.subtotal
  // exactly — no rounding drift between the label and the arithmetic.
  const requiredSubtotal = useMemo(
    () =>
      requiredItems.reduce((sum, line) => sum + resolveEstimateLineAmounts(line).lineSubtotal, 0),
    [requiredItems],
  );
  const selectedOptionalRows = useMemo(
    () =>
      optionalOffers.flatMap((offer, index) => {
        const on = Boolean(optionalSelections[optionalKey(offer.parent, index)]);
        if (!on) return [];
        const lines = offerCountedLines(offer, true);
        const subtotal = lines.reduce(
          (sum, line) => sum + resolveEstimateLineAmounts(line).lineSubtotal,
          0,
        );
        return [{ name: offer.parent.name, subtotal, lines }];
      }),
    [optionalOffers, optionalSelections],
  );
  // ONE predicate for "is this line currently selected", shared by the
  // membership total, the derived snapshot and `selectedEstimate`. They drifted
  // apart when each computed it its own way.
  //
  // Resolved by IDENTITY against the lines that produced the totals, not by a
  // per-line rule: `selectedLines` holds the very objects from
  // `estimate.lineItems`, so the predicate cannot disagree with the arithmetic
  // it is meant to describe — including for a part, whose membership is decided
  // by the service that carries it rather than by its own `isOptional`.
  const selectedLineSet = useMemo(() => new Set(selectedLines), [selectedLines]);
  const isLineSelected = useCallback(
    (line: Estimate['lineItems'][number]) => selectedLineSet.has(line),
    [selectedLineSet],
  );
  // How many SELECTED rows carry each lineItemId — a multiset, so the legacy
  // unaligned-snapshot path can consume duplicate `lineDiscounts` entries at most
  // once instead of keeping both when two rows share an id and only one is in the
  // sale. The membership total and the per-line snapshot filter both key off this.
  const selectedLineIdCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const line of selectedLines) {
      counts.set(line.lineItemId, (counts.get(line.lineItemId) ?? 0) + 1);
    }
    return counts;
  }, [selectedLines]);
  // NOT-245: the backend pricing engine bakes every discount (line, membership,
  // and the distributed document discount) into each line's `lineTotal`, so
  // `grandTotal = Σ lineTotal`. The old code subtracted the document/membership
  // discounts again on top of that → double-counted (EST-000034 showed $80 vs
  // the persisted $90). The discount/membership rows below stay as an
  // informational breakdown; the authoritative total is the sum of lineTotals.
  const selectedGrandTotal = Math.max(totals.total, 0);
  // The two discount rows are a SPLIT of this one number, so it is computed once
  // and handed to both. Computing it twice is how they came to disagree: the
  // membership row was free to print more than the document gave away, and the
  // "Discount" row silently absorbed the difference into a floor at zero.
  const selectedSavingsGap = documentSavingsGap({
    subtotal: totals.subtotal,
    taxTotal: totals.taxTotal,
    grandTotal: selectedGrandTotal,
  });
  const selectedMembershipDiscountTotal = selectedMembershipDiscount(
    { ...estimate, lineItems },
    isLineSelected,
    selectedLineIdCounts,
    selectedSavingsGap,
  );
  // The "Discount" row: every saving except the membership's, for the lines
  // currently in the sale. Derived from the three totals printed around it so
  // the column always adds up — see the helper for why neither the stored
  // column nor `discountType`/`discountValue` can answer this. It is the
  // remainder of the gap, so it has to come after the membership row.
  const selectedDiscountTotal = selectedNonMembershipDiscount({
    subtotal: totals.subtotal,
    taxTotal: totals.taxTotal,
    grandTotal: selectedGrandTotal,
    membershipDiscountTotal: selectedMembershipDiscountTotal,
  });
  // Derived, not read from `estimate.depositAmount` — see the helper for why a
  // PERCENT deposit otherwise looks like a flat fee while the customer toggles
  // optional items. `selectedLines` lets the helper subtract any membership line
  // from the base (NOT-604): a membership is a subscription, not deposit-eligible
  // work, and the server prices the deposit without it.
  const selectedDeposit = useMemo(
    () =>
      selectedDepositAmount({
        depositAmount: estimate.depositAmount,
        depositType: estimate.depositType,
        depositValue: estimate.depositValue,
        depositCollected: estimate.depositCollected,
        selectedGrandTotal,
        selectedLines,
      }),
    [
      estimate.depositAmount,
      estimate.depositCollected,
      estimate.depositType,
      estimate.depositValue,
      selectedGrandTotal,
      selectedLines,
    ],
  );
  const selectedMembershipPlanSnapshot = useMemo(() => {
    if (!estimate.membershipPlanSnapshot) return null;
    // NOT-625 — a non-member snapshot carries only `offer`, no member-pricing
    // fields, so the discount plumbing below must tolerate their absence.
    const lineDiscounts = estimate.membershipPlanSnapshot.lineDiscounts ?? [];
    // Filtered with the SAME predicate that produced
    // `selectedMembershipDiscountTotal`. Keying on `lineItemId` here left the
    // snapshot carrying both entries for two rows sharing a catalog item while
    // the total counted one, so `selectedEstimate` disagreed with itself — and
    // the customer PDF renders from this snapshot.
    const aligned = lineDiscounts.length === lineItems.length;
    const keptUnaligned = aligned
      ? null
      : keptUnalignedDiscountIndices(lineDiscounts, selectedLineIdCounts);
    const next = {
      ...estimate.membershipPlanSnapshot,
      totalDiscount: selectedMembershipDiscountTotal,
      lineDiscounts: lineDiscounts.filter((discount, index) => {
        if (aligned) {
          const line = lineItems[index];
          return !line || isLineSelected(line);
        }
        return keptUnaligned!.has(index);
      }),
    };
    // Keep downloads and signature previews aligned with the live offer after
    // the customer changes add-ons. The approval endpoint remains the authority
    // that reprices and persists the final selected scope.
    if (optionalSelectionsChanged && next.offer) {
      next.offer =
        membershipOfferForSelectedLines(next.offer, selectedLines, lineItems) ?? undefined;
    }
    return next;
  }, [
    estimate.membershipPlanSnapshot,
    lineItems,
    isLineSelected,
    optionalSelectionsChanged,
    selectedLines,
    selectedMembershipDiscountTotal,
    selectedLineIdCounts,
  ]);
  // What the approval SUBMITS for each line: the customer's toggle, not the
  // line's membership in the sale. The two differ for a part under a declined
  // service — out of the sale, but not itself optional, and its own row still
  // says selected because nothing ever wrote to it. The server derives the
  // cascade again when it reprices, from the service's toggle alone.
  const approvalLineItems = useMemo(
    () =>
      lineItems.map((li) => ({
        lineItemId: li.lineItemId,
        ...(li.lineKey ? { lineKey: li.lineKey } : {}),
        // A non-optional line submits ITS OWN stored flag, never a healed one:
        // a part under a declined service still reads `true` (nothing wrote to
        // it — that is what makes the cascade derived), while a legacy row
        // actually stored `false` submits `false` and `customerApprove`
        // refuses the approval by name. Healing it here would have the
        // customer sign the priced total and the server persist a higher one.
        isSelected: li.isOptional ? isLineSelected(li) : li.isSelected !== false,
      })),
    [lineItems, isLineSelected],
  );
  const selectedEstimate = useMemo<Estimate>(
    () => ({
      ...estimate,
      lineItems: lineItems.map((li) => ({
        ...li,
        ...selectionPricing?.get(li),
        isSelected: isLineSelected(li),
      })),
      subtotal: totals.subtotal,
      taxTotal: totals.taxTotal,
      discountTotal: selectedDiscountTotal,
      membershipDiscountTotal: selectedMembershipDiscountTotal,
      membershipPlanSnapshot: selectedMembershipPlanSnapshot,
      grandTotal: selectedGrandTotal,
      // Keeps the downloaded and the signature-consent PDFs internally
      // consistent: both print this object's deposit next to this object's
      // total, so a percentage deposit rendered against the old total would
      // put two figures that don't agree on the document the customer signs.
      depositAmount: selectedDeposit,
    }),
    [
      estimate,
      lineItems,
      // `isLineSelected` closes over `optionalSelections`, so depending on both
      // would recompute twice for one change.
      isLineSelected,
      selectedDeposit,
      selectedDiscountTotal,
      selectedGrandTotal,
      selectedMembershipDiscountTotal,
      selectedMembershipPlanSnapshot,
      selectionPricing,
      totals.subtotal,
      totals.taxTotal,
    ],
  );

  return {
    requiredItems,
    optionalOffers,
    optionalSelections,
    toggleOptional,
    requiredRows,
    displayOptionalOffers,
    optionalSelectionsChanged,
    selectedLines,
    totals,
    requiredSubtotal,
    selectedOptionalRows,
    isLineSelected,
    approvalLineItems,
    selectedGrandTotal,
    selectedMembershipDiscountTotal,
    selectedDiscountTotal,
    selectedDeposit,
    selectedMembershipPlanSnapshot,
    selectedEstimate,
  };
}

/** Everything the selection engine derives — the shared surfaces' single source of sale truth. */
export type EstimateSelections = ReturnType<typeof useEstimateSelections>;
